import { useEffect } from 'react'

import { useManagementCancelOrder } from '@apps-common/apis/management/order/cancel-order'
import { useManagementDeleteOrder } from '@apps-common/apis/management/order/delete-order'
import { QUERY_SEARCH_ORDERS } from '@apps-common/apis/portal/orders'
import { QUERY_GET_ORDER_DETAILS } from '@apps-common/apis/portal/orders/get-order-details'
import { QUERY_SEARCH_ORDERS_COUNT } from '@apps-common/apis/portal/orders/search-orders-count'
import { QUERY_SEARCH_ORDERS_INFINITE } from '@apps-common/apis/portal/orders/search-orders-infinite'
import { ButtonRoundLg } from '@apps-common/components/elements/button/ButtonRoundLg'
import { ConfirmModal, ConfirmModalAction } from '@apps-common/components/modals/ConfirmModal'
import { ModalButton } from '@apps-common/components/modals/ModalButton'
import { commonIcons } from '@apps-common/config/icons'
import { OrderStatus } from '@apps-common/config/orders'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'

interface IProps {
  loading: boolean
  updating: boolean
  orderId: string
  status?: string
  isUpdatingCallback: (isUpdating: boolean) => void
}

export const ManagementOrderDetailsOptions: React.FC<IProps> = ({ loading, updating, orderId, status, isUpdatingCallback }) => {
  const { navigateBack } = useNavigation()
  const { mutate: cancelOrder, isPending: cancelOrderIsPending } = useManagementCancelOrder(true, () => {
    queryClient.refetchQueries({ queryKey: QUERY_GET_ORDER_DETAILS(orderId) })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS_COUNT() })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS() })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS_INFINITE() })
  })
  const { mutate: deleteOrder, isPending: deleteOrderIsPending } = useManagementDeleteOrder(true, () => {
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS_COUNT() })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS() })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS_INFINITE() })
    navigateBack()
  })

  useEffect(() => {
    isUpdatingCallback(cancelOrderIsPending || deleteOrderIsPending)
  }, [cancelOrderIsPending, deleteOrderIsPending])

  const optionsDisabled = loading || updating || status !== OrderStatus.pending

  const CancelIcon = commonIcons.cancel
  const DeleteIcon = commonIcons.delete

  return (
    <>
      <ModalButton
        component={ConfirmModal}
        disabled={optionsDisabled}
        componentProps={{
          text: 'Cancel',
          description: 'Confirm you would like to cancel this order.',
          action: ConfirmModalAction.Warning
        }}
        confirmCallback={() => cancelOrder({ orderId })}
      >
        <ButtonRoundLg type='div' className='action-neutral' disabled={optionsDisabled}>
          <CancelIcon className='text-[1.1em]' />
        </ButtonRoundLg>
      </ModalButton>
      <ModalButton
        component={ConfirmModal}
        disabled={optionsDisabled}
        componentProps={{
          text: 'Delete',
          description: 'Confirm you would like to delete this order.',
          action: ConfirmModalAction.Error
        }}
        confirmCallback={() => deleteOrder({ orderId })}
      >
        <ButtonRoundLg type='div' className='action-error' disabled={optionsDisabled}>
          <DeleteIcon className='text-[1.2em]' />
        </ButtonRoundLg>
      </ModalButton>
    </>
  )
}
